import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectTrigger, SelectContent, SelectItem, SelectValue,
} from "@/components/ui/select";
import { Download, Sparkles, Plus, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const PAID_TIERS = [
  { id: "focus-web", name: "Focus Web", price: 14, perUser: false },
  { id: "focus", name: "Focus", price: 23, perUser: false },
  { id: "sync-web", name: "Sync Web", price: 18, perUser: true },
  { id: "sync", name: "Sync", price: 28, perUser: true },
  { id: "momentum", name: "Momentum", price: 38, perUser: true },
];
const TIER_PRICES = {
  spark: 0, "focus-web": 14, focus: 23, "sync-web": 18, sync: 28, momentum: 38, command: null,
};

const BLOCKS = [
  { name: "Social Media block", items: "X.com, Facebook, Instagram", price: 6 },
  { name: "Design Apps block", items: "Figma, Adobe XD, Sketch", price: 8 },
  { name: "Video block", items: "YouTube, Vimeo, TikTok", price: 6 },
];

const INVOICES = [
  { id: "INV-2026-005", date: "1 May 2026", amount: 23.00, status: "Paid" },
  { id: "INV-2026-004", date: "1 Apr 2026", amount: 23.00, status: "Paid" },
  { id: "INV-2026-003", date: "1 Mar 2026", amount: 23.00, status: "Paid" },
];

export default function Billing() {
  const { user, refresh } = useAuth();
  const [annual, setAnnual] = useState(user?.billing_period === "annual");
  const [hasCompass, setHasCompass] = useState(!!user?.compass_enabled);
  const [selectedTier, setSelectedTier] = useState(
    user?.tier && user.tier !== "spark" && user.tier !== "command" ? user.tier : "focus",
  );
  const [seats, setSeats] = useState(user?.seats || 1);
  const [loading, setLoading] = useState(false);
  const [polling, setPolling] = useState(false);
  const [params, setParams] = useSearchParams();

  const tierPrice = TIER_PRICES[user?.tier] ?? 23;
  const monthly = annual ? Math.round((tierPrice * 10) / 12) : tierPrice;

  const selectedTierObj = PAID_TIERS.find((t) => t.id === selectedTier) || PAID_TIERS[1];
  const seatsForCalc = selectedTierObj.perUser ? seats : 1;
  const perMonth = (selectedTierObj.price + (hasCompass ? 9 : 0)) * seatsForCalc;
  const checkoutAmount = annual ? perMonth * 10 : perMonth;

  // ---------- Payment status polling after Stripe redirect ----------
  useEffect(() => {
    const sessionId = params.get("session_id");
    const cancelled = params.get("cancelled");
    if (cancelled) {
      toast.info("Checkout cancelled.");
      params.delete("cancelled");
      setParams(params, { replace: true });
      return;
    }
    if (!sessionId) return;
    let attempts = 0;
    setPolling(true);
    const poll = async () => {
      attempts += 1;
      try {
        const { data } = await api.get(`/stripe/status/${sessionId}`);
        if (data.payment_status === "paid") {
          toast.success("Payment received. Your plan is upgraded.");
          await refresh();
          params.delete("session_id");
          setParams(params, { replace: true });
          setPolling(false);
          return;
        }
        if (data.status === "expired") {
          toast.error("Checkout session expired.");
          setPolling(false);
          return;
        }
        // status==initiated/pending or data.pending===true → just retry
      } catch (e) {
        // ignore, try again
      }
      if (attempts < 12) setTimeout(poll, 2000);
      else {
        toast.info("Still processing - refresh in a minute to see the new plan.");
        setPolling(false);
      }
    };
    poll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---------- Initiate checkout ----------
  const startCheckout = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/stripe/create-checkout-session", {
        tier_id: selectedTier,
        billing_period: annual ? "annual" : "monthly",
        seats: seatsForCalc,
        compass: hasCompass,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start checkout.");
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6 max-w-5xl" data-testid="billing-page">
      <div>
        <p className="text-xs uppercase tracking-[0.2em] text-[#2C6BAC] font-semibold">Billing</p>
        <h1 className="font-display text-3xl text-[#1A1A2E] mt-1">Plan, invoices, and add-ons.</h1>
      </div>

      {polling && (
        <div className="rounded-xl border border-[#1D9E75]/30 bg-[#1D9E75]/10 p-4 flex items-center gap-3" data-testid="billing-polling">
          <Loader2 className="animate-spin text-[#1D9E75]" size={16} />
          <p className="text-sm text-[#1A1A2E]">Confirming payment with Stripe…</p>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <section className="lg:col-span-2 rounded-xl border border-[#E8ECF2] bg-white p-6">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-xs uppercase tracking-[0.18em] text-[#2C6BAC] font-semibold">Current plan</p>
              <h2 className="font-display text-2xl text-[#1A1A2E] mt-1">WYLO {(user?.tier || "spark").toUpperCase()}</h2>
              <p className="text-sm text-[#555568] mt-1">
                {user?.billing_period === "annual" ? "Annual · 2 months free" : "Monthly billing"}{" · "}
                {user?.seats || 1} seat{(user?.seats || 1) > 1 ? "s" : ""}
              </p>
            </div>
            <div className="text-right">
              <p className="font-display text-3xl font-bold text-[#1A1A2E]">${monthly || 0}</p>
              <p className="text-xs text-[#555568]">/ month</p>
            </div>
          </div>
        </section>

        <section className="rounded-xl bg-gradient-to-br from-[#1A3A5C] to-[#2C6BAC] text-white p-6 flex flex-col">
          <div className="flex items-center gap-2 mb-1">
            <Sparkles size={16} />
            <p className="text-xs uppercase tracking-[0.18em] font-semibold">Add-on</p>
          </div>
          <h2 className="font-display text-2xl">WYLO Compass</h2>
          <p className="text-sm text-[#E8ECF2]/85 mt-2 flex-1">
            AI-enhanced reports, behavioural modelling, and the Compass chatbot. Bundled into checkout below.
          </p>
          <div className="mt-4 flex items-center justify-between">
            <p className="font-mono text-2xl">$9 <span className="text-xs text-[#E8ECF2]/70">/user/mo</span></p>
            <div className="flex items-center gap-2 text-xs">
              <span>{hasCompass ? "Included" : "Not added"}</span>
              <Switch checked={hasCompass} onCheckedChange={setHasCompass} data-testid="billing-compass-toggle" />
            </div>
          </div>
        </section>
      </div>

      {/* Choose plan + checkout */}
      <section className="rounded-xl border border-[#E8ECF2] bg-white p-6">
        <h2 className="font-display text-xl font-bold text-[#1A1A2E] mb-2">Change plan</h2>
        <p className="text-sm text-[#555568] mb-5">Annual is 2 months free - pay for 10, get 12.</p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="text-xs uppercase tracking-[0.18em] text-[#555568] font-semibold">Tier</label>
            <Select value={selectedTier} onValueChange={setSelectedTier}>
              <SelectTrigger data-testid="billing-tier-select" className="mt-2">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PAID_TIERS.map((t) => (
                  <SelectItem key={t.id} value={t.id}>
                    {t.name} - ${t.price}{t.perUser ? "/user" : ""}/mo
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="text-xs uppercase tracking-[0.18em] text-[#555568] font-semibold">Seats</label>
            <input
              type="number"
              min={1}
              max={100}
              disabled={!selectedTierObj.perUser}
              value={seats}
              onChange={(e) => setSeats(Math.max(1, Math.min(100, parseInt(e.target.value || "1", 10))))}
              className="mt-2 w-full rounded-md border border-[#E8ECF2] bg-white px-3 py-2 text-sm disabled:bg-[#F8F9FC] disabled:text-[#555568]"
              data-testid="billing-seats"
            />
            {!selectedTierObj.perUser && <p className="text-[10px] text-[#555568] mt-1">Single-user tier</p>}
          </div>

          <div>
            <label className="text-xs uppercase tracking-[0.18em] text-[#555568] font-semibold">Billing period</label>
            <div className="mt-2 inline-flex items-center gap-3 rounded-md border border-[#E8ECF2] bg-white px-3 py-2 w-full justify-between">
              <span className={`text-sm ${!annual ? "text-[#1A1A2E] font-semibold" : "text-[#555568]"}`}>Monthly</span>
              <Switch checked={annual} onCheckedChange={setAnnual} data-testid="billing-toggle" />
              <span className={`text-sm ${annual ? "text-[#1A1A2E] font-semibold" : "text-[#555568]"}`}>
                Annual <span className="text-[#1D9E75] text-xs">· 2 mo free</span>
              </span>
            </div>
          </div>
        </div>

        <div className="mt-6 rounded-md bg-[#F8F9FC] border border-[#E8ECF2] p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div className="text-sm text-[#1A1A2E]">
            <p>
              {selectedTierObj.name}{hasCompass && " + Compass"}{" · "}{seatsForCalc} seat{seatsForCalc > 1 ? "s" : ""}{" · "}
              {annual ? "Annual (10 months billed)" : "Monthly"}
            </p>
            <p className="font-display text-2xl font-bold text-[#1A1A2E] mt-1">
              ${checkoutAmount.toFixed(2)}
              <span className="text-xs text-[#555568] ml-1">{annual ? "today (covers 12 months)" : "/month"}</span>
            </p>
          </div>
          <Button
            onClick={startCheckout}
            disabled={loading}
            className="bg-[#2C6BAC] hover:bg-[#1A3A5C] text-white"
            data-testid="billing-checkout-btn"
          >
            {loading ? <><Loader2 className="animate-spin mr-2" size={16} />Opening checkout…</> : "Proceed to Stripe checkout"}
          </Button>
        </div>
        <p className="text-[10px] text-[#555568] mt-2 font-mono">Test mode · use Stripe test card 4242 4242 4242 4242, any future date, any CVC.</p>
      </section>

      {/* Add-on blocks */}
      <section className="rounded-xl border border-[#E8ECF2] bg-white p-6">
        <h2 className="font-display text-xl font-bold text-[#1A1A2E] mb-5">Add-on blocks</h2>
        <p className="text-sm text-[#555568] mb-4">Need more slots in your hierarchy? Purchase a category block of three.</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {BLOCKS.map((b) => (
            <div key={b.name} className="rounded-md border border-[#E8ECF2] p-5 bg-[#F8F9FC]">
              <p className="font-display font-bold text-[#1A1A2E]">{b.name}</p>
              <p className="text-xs text-[#555568] mt-1">{b.items}</p>
              <div className="mt-4 flex items-center justify-between">
                <p className="font-mono text-lg text-[#1A3A5C]">${b.price}/mo</p>
                <Button size="sm" variant="outline" onClick={() => toast.info("Block purchase: coming soon - for now, contact us.")}>
                  <Plus size={14} className="mr-1" /> Add
                </Button>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Invoices */}
      <section className="rounded-xl border border-[#E8ECF2] bg-white p-6">
        <h2 className="font-display text-xl font-bold text-[#1A1A2E] mb-5">Invoices</h2>
        <table className="w-full text-sm">
          <thead className="text-left text-[#555568]">
            <tr><th className="py-2 font-medium">Invoice</th><th className="font-medium">Date</th><th className="font-medium">Amount</th><th className="font-medium">Status</th><th></th></tr>
          </thead>
          <tbody>
            {INVOICES.map((i) => (
              <tr key={i.id} className="border-t border-[#E8ECF2]">
                <td className="py-3 font-mono text-xs">{i.id}</td>
                <td>{i.date}</td>
                <td className="font-mono">${i.amount.toFixed(2)}</td>
                <td><span className="text-xs uppercase tracking-wider text-[#1D9E75] font-semibold">{i.status}</span></td>
                <td className="text-right"><Button size="sm" variant="ghost"><Download size={14} /></Button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </div>
  );
}
